import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "../Components/Header";
import "./EditProduct.css";
import axios from "axios";
import { API_BASE } from "../config";

const MAX_IMAGES = 6;
const MAX_IMAGE_SIZE_KB = 1000;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_KB * 1024;

const DEFAULT_DETAILS = {
  length: "",
  washCare: "",
  sleeves: "",
  neckline: "",
  dressInfo: "",
  disclaimer: "",
  whatYouWillReceive: "",
  customDetails: [],
};

const DEFAULT_COLOR_OPTIONS = [
  { name: "Red", hex: "#D32F2F" },
  { name: "Maroon", hex: "#800000" },
  { name: "Pink", hex: "#EC407A" },
  { name: "Orange", hex: "#F57C00" },
  { name: "Yellow", hex: "#FBC02D" },
  { name: "Green", hex: "#388E3C" },
  { name: "Blue", hex: "#1976D2" },
  { name: "Navy", hex: "#1A237E" },
  { name: "Purple", hex: "#7B1FA2" },
  { name: "Black", hex: "#111111" },
  { name: "White", hex: "#FFFFFF" },
  { name: "Beige", hex: "#D7CCC8" },
  { name: "Gold", hex: "#D4AF37" },
  { name: "Grey", hex: "#757575" },
];

const CATEGORY_OPTIONS = [
  { id: "1", name: "Sarees" },
  { id: "2", name: "Dresses" },
  { id: "3", name: "Kurti" },
  { id: "4", name: "Lehenga" },
];

const normalizeSizes = (sizes) => {
  if (!Array.isArray(sizes)) return [];

  return sizes
    .map((item) =>
      typeof item === "string" ? item : item?.size
    )
    .filter(Boolean);
};

const normalizeDetails = (details) => {
  if (!details) {
    return {
      ...DEFAULT_DETAILS,
      customDetails: [],
    };
  }

  return {
    length: details.length ?? "",
    washCare: details.washCare ?? "",
    sleeves: details.sleeves ?? "",
    neckline: details.neckline ?? "",
    dressInfo: details.dressInfo ?? "",
    disclaimer: details.disclaimer ?? "",
    whatYouWillReceive: details.whatYouWillReceive ?? "",
    customDetails: Array.isArray(details.customDetails)
      ? details.customDetails.map((item) => ({
        label: item?.label ?? "",
        value: item?.value ?? "",
      }))
      : [],
  };
};

const EditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [showPopup, setShowPopup] = useState(false);

  const [colorDropdownOpen, setColorDropdownOpen] =
    useState(false);

  const [customColor, setCustomColor] = useState("");

  const [customColorHex, setCustomColorHex] =
    useState("#A21803");

  const [colorOptions, setColorOptions] = useState(
    DEFAULT_COLOR_OPTIONS
  );

  const [product, setProduct] = useState({
    id: "",
    name: "",

    categoryId: "",
    category: "",
    subCategory: "",

    originalPrice: "",
    discountPercentage: "",
    finalPrice: "",

    description: "",
    material: "",
    stock: "",
    sku: "",
    weight: "",
    size: "",

    sizes: [],
    tag: "",

    images: [],
    colors: [],

    status: 1,

    details: {
      ...DEFAULT_DETAILS,
    },
  });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(
          `${API_BASE}/Products/${id}`
        );

        const data = response.data;

        const backendColors = Array.isArray(data.colors)
          ? data.colors
          : [];

        const backendColorNames = backendColors
          .map((item) => item?.colorName)
          .filter(Boolean);

        setColorOptions((prev) => {
          const next = [...prev];

          backendColorNames.forEach((colorName) => {
            if (
              !next.some(
                (item) =>
                  item.name.toLowerCase() ===
                  colorName.toLowerCase()
              )
            ) {
              next.push({
                name: colorName,
                hex: colorName,
              });
            }
          });

          return next;
        });

        setProduct({
          id: data.id,

          name: data.name ?? "",

          categoryId: data.categoryId ?? "",

          category: data.category?.name ?? "",

          originalPrice: data.originalPrice ?? 0,

          discountPercentage:
            data.discountPercentage ?? "",

          finalPrice:
            data.finalPrice ??
            data.originalPrice ??
            0,

          description: data.description ?? "",

          material: data.material ?? "",

          stock: data.quantity ?? 0,

          sku: data.sku ?? "",

          weight: data.weight ?? 0,

          size: data.size ?? "",

          sizes: normalizeSizes(data.sizes),

          tag: data.tag ?? "",

          status: Number(data.status ?? 1),

          images: Array.isArray(data.images)
            ? data.images.slice(0, MAX_IMAGES)
            : [],

          colors: backendColors,

          details: normalizeDetails(data.details),
        });
      } catch (error) {
        console.error(
          "Error fetching product",
          error
        );

        alert(
          error?.response?.data?.message ||
          "Failed to load product"
        );
      }
    };

    fetchProduct();
  }, [id]);

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setProduct((prev) => ({
      ...prev,

      [name]:
        name === "status"
          ? Number(value)
          : value,
    }));
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;

    const selected = CATEGORY_OPTIONS.find(
      (item) => item.id === value
    );

    setProduct((prev) => ({
      ...prev,

      categoryId: value,

      category:
        selected?.name ||
        prev.category,
    }));
  };

  const handleDetailChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setProduct((prev) => ({
      ...prev,

      details: {
        ...prev.details,

        [name]: value,
      },
    }));
  };

  const handleAddCustomDetail = () => {
    setProduct((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        customDetails: [
          ...(prev.details.customDetails || []),
          { label: "", value: "" },
        ],
      },
    }));
  };

  const handleCustomDetailChange = (index, field, value) => {
    setProduct((prev) => {
      const customDetails = [...(prev.details.customDetails || [])];
      customDetails[index] = {
        ...customDetails[index],
        [field]: value,
      };

      return {
        ...prev,
        details: {
          ...prev.details,
          customDetails,
        },
      };
    });
  };

  const handleRemoveCustomDetail = (index) => {
    setProduct((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        customDetails: (prev.details.customDetails || []).filter(
          (_, detailIndex) => detailIndex !== index
        ),
      },
    }));
  };

  const handleColorToggle = (colorName) => {
    setProduct((prev) => {
      const colors = Array.isArray(prev.colors)
        ? prev.colors
        : [];

      const exists = colors.some(
        (item) =>
          item?.colorName?.toLowerCase() ===
          colorName.toLowerCase()
      );

      return {
        ...prev,

        colors: exists
          ? colors.filter(
            (item) =>
              item?.colorName?.toLowerCase() !==
              colorName.toLowerCase()
          )
          : [
            ...colors,

            {
              colorName,

              colorImageUrl:
                prev.images?.[0]
                  ?.imageUrl || "",
            },
          ],
      };
    });
  };

  const handleAddColor = () => {
    const colorName = customColor.trim();

    if (!colorName) return;

    if (
      !colorOptions.some(
        (item) =>
          item.name.toLowerCase() ===
          colorName.toLowerCase()
      )
    ) {
      setColorOptions((prev) => [
        ...prev,

        {
          name: colorName,
          hex: customColorHex,
        },
      ]);
    }

    setProduct((prev) => {
      const exists = prev.colors.some(
        (item) =>
          item?.colorName?.toLowerCase() ===
          colorName.toLowerCase()
      );

      if (exists) {
        return prev;
      }

      return {
        ...prev,

        colors: [
          ...prev.colors,

          {
            colorName,

            colorImageUrl:
              prev.images?.[0]
                ?.imageUrl || "",
          },
        ],
      };
    });

    setCustomColor("");
  };

  const handleImageUpload = async (
    e,
    index
  ) => {
    try {
      const file = e.target.files[0];

      if (!file) return;

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        alert(`Image must be below ${MAX_IMAGE_SIZE_KB} KB.`);
        e.target.value = "";
        return;
      }

      const uploadFormData =
        new FormData();

      uploadFormData.append(
        "file",
        file
      );

      const response =
        await axios.post(
          `${API_BASE}/Upload`,
          uploadFormData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

      const imageUrl =
        response.data.imageUrl ||
        response.data.url;

      setProduct((prev) => {
        const updatedImages = [
          ...prev.images,
        ];

        const newImage = {
          imageUrl,
          isMain: false,
          imageType: "Product",
        };

        if (index < updatedImages.length) {
          updatedImages[index] = newImage;
        } else if (updatedImages.length < MAX_IMAGES) {
          updatedImages.push(newImage);
        }

        return {
          ...prev,
          images: updatedImages
            .slice(0, MAX_IMAGES)
            .map((img, imageIndex) => ({
              ...img,
              isMain: imageIndex === 0,
            })),
        };
      });

      e.target.value = "";
    } catch (error) {
      console.error(error);

      alert(
        "Image upload failed"
      );
    }
  };

  const handleRemoveImage = (index) => {
    setProduct((prev) => ({
      ...prev,
      images: prev.images
        .filter((_, imageIndex) => imageIndex !== index)
        .map((img, imageIndex) => ({
          ...img,
          isMain: imageIndex === 0,
        })),
    }));
  };

  const handleSaveClick = async () => {
    try {
      // -----------------------------
      // Frontend Validation
      // -----------------------------

      if (!product.name.trim()) {
        alert("Product name is required.");
        return;
      }

      if (!product.description.trim()) {
        alert("Product description is required.");
        return;
      }

      if (!product.categoryId) {
        alert("Category is required.");
        return;
      }

      if (
        product.originalPrice === "" ||
        Number(product.originalPrice) <= 0
      ) {
        alert(
          "Original price must be greater than 0."
        );
        return;
      }

      if (
        product.stock === "" ||
        Number(product.stock) < 0
      ) {
        alert(
          "Quantity cannot be negative."
        );
        return;
      }

      const discount =
        product.discountPercentage === ""
          ? null
          : Number(product.discountPercentage);

      if (
        discount !== null &&
        (discount < 0 || discount > 100)
      ) {
        alert(
          "Discount percentage must be between 0 and 100."
        );
        return;
      }

      // -----------------------------
      // Clean Custom Details
      // -----------------------------

      const cleanedCustomDetails = (
        product.details?.customDetails || []
      )
        .filter(
          (item) =>
            item?.label?.trim() ||
            item?.value?.trim()
        )
        .map((item) => ({
          label: item?.label?.trim() || "",
          value: item?.value?.trim() || "",
        }));

      // -----------------------------
      // PUT Payload
      // -----------------------------

      const payload = {
        name: product.name.trim(),

        description: product.description.trim(),

        categoryId: Number(product.categoryId),

        subCategory:
          product.subCategory?.trim() || "",

        // -----------------------------
        // Pricing
        // -----------------------------

        originalPrice:
          Number(product.originalPrice),

        discountPercentage: discount,

        // DO NOT SEND price/finalPrice
        // Backend calculates FinalPrice

        // -----------------------------
        // Stock
        // -----------------------------

        quantity: Number(product.stock),

        sku:
          product.sku?.trim() || "",

        weight:
          Number(product.weight || 0),

        material:
          product.material?.trim() || "",

        size:
          product.size?.trim() || "",

        sizes:
          Array.isArray(product.sizes)
            ? product.sizes
            : [],

        status:
          Number(product.status),

        tag:
          product.tag?.trim() || "",

        // -----------------------------
        // Images
        // -----------------------------

        images: (product.images || [])
          .filter((img) => {
            const imageUrl =
              typeof img === "string"
                ? img
                : img?.imageUrl;

            return (
              imageUrl &&
              !imageUrl.startsWith("data:image")
            );
          })
          .map((img, index) => ({
            imageUrl:
              typeof img === "string"
                ? img
                : img.imageUrl,

            isMain: index === 0,

            imageType:
              typeof img === "object" &&
                img?.imageType
                ? img.imageType
                : "Product",
          })),

        // -----------------------------
        // Colors
        // -----------------------------

        colors: (product.colors || [])
          .filter(
            (color) =>
              color?.colorName?.trim()
          )
          .map((color) => ({
            colorName:
              color.colorName.trim(),

            colorImageUrl:
              color.colorImageUrl || "",
          })),

        // -----------------------------
        // Product Details
        // -----------------------------

        details: {
          length:
            product.details?.length?.trim() || "",

          washCare:
            product.details?.washCare?.trim() || "",

          sleeves:
            product.details?.sleeves?.trim() || "",

          neckline:
            product.details?.neckline?.trim() || "",

          dressInfo:
            product.details?.dressInfo?.trim() || "",

          disclaimer:
            product.details?.disclaimer?.trim() || "",

          whatYouWillReceive:
            product.details?.whatYouWillReceive?.trim() || "",

          customDetails:
            cleanedCustomDetails,
        },
      };

      console.log(
        "Updating product:",
        payload
      );

      const response = await axios.put(
        `${API_BASE}/Products/${id}`,
        payload
      );

      console.log(
        "Update response:",
        response.data
      );

      // Backend-calculated price can be used
      // to keep frontend state accurate.
      if (
        response.data?.finalPrice !== undefined
      ) {
        setProduct((prev) => ({
          ...prev,

          finalPrice:
            response.data.finalPrice,

          originalPrice:
            response.data.originalPrice ??
            prev.originalPrice,

          discountPercentage:
            response.data.discountPercentage ??
            "",
        }));
      }

      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);

        navigate(
          `/product-details/${id}`
        );
      }, 1500);
    } catch (error) {
      console.error(
        "Error updating product:",
        error
      );

      const backendMessage =
        error?.response?.data;

      alert(
        typeof backendMessage === "string"
          ? backendMessage
          : backendMessage?.message ||
          "Failed to update product"
      );
    }
  };

  const selectedColorNames =
    product.colors
      .map(
        (item) =>
          item?.colorName
      )
      .filter(Boolean);

  const categoryIdString =
    String(
      product.categoryId || ""
    );

  const isKnownCategory =
    CATEGORY_OPTIONS.some(
      (item) =>
        item.id ===
        categoryIdString
    );


  const originalPrice = Number(product.originalPrice) || 0;

  const discountPercentage =
    product.discountPercentage === ""
      ? 0
      : Number(product.discountPercentage) || 0;

  const calculatedFinalPrice =
    originalPrice -
    (originalPrice * discountPercentage) / 100;

  const discountAmount =
    originalPrice - calculatedFinalPrice;

  return (
    <div className="editproduct-container">
      <Header />

      <div className="editproduct-content">
        {/* ================= HEADER ================= */}

        <div className="editproduct-top">
          <button
            type="button"
            className="editproduct-back-btn"
            onClick={() =>
              navigate("/products")
            }
          >
            ←
          </button>

          <div>
            <h2>Edit Product</h2>

            <p>
              Update product details below
            </p>
          </div>
        </div>

        {/* ================= PRODUCT IMAGES ================= */}

        <div className="editproduct-form-card">
          <h3>
            Product Images
          </h3>

          <div className="editproduct-images-row">
            {Array.from({ length: MAX_IMAGES }, (_, index) => {
              const image =
                product.images[index];

              return (
                <div
                  key={index}
                  className="image-upload-wrapper"
                >
                  <input
                    type="file"
                    accept="image/*"
                    id={`imageUpload${index}`}
                    hidden
                    onChange={(e) =>
                      handleImageUpload(
                        e,
                        index
                      )
                    }
                  />

                  <label
                    htmlFor={`imageUpload${index}`}
                    className="image-upload-box"
                  >
                    {image ? (
                      <img
                        src={
                          image.imageUrl ||
                          image
                        }
                        alt={`preview-${index + 1
                          }`}
                        className="editproduct-preview-img"
                      />
                    ) : (
                      <div className="empty-image-box">
                        +
                      </div>
                    )}
                  </label>

                  {image && (
                    <button
                      type="button"
                      className="editproduct-image-remove-btn"
                      onClick={() => handleRemoveImage(index)}
                      title="Remove image"
                      aria-label={`Remove image ${index + 1}`}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="editproduct-upload-info">
            <p>
              Click on image box to upload or replace image
            </p>

            <span>
              Max 6 images • Each new image below 1000 KB • {product.images.length}/6 selected
            </span>
          </div>
        </div>

        {/* ================= BASIC INFORMATION ================= */}

        <div className="editproduct-form-card">
          <h3>
            Basic Information
          </h3>

          <label className="editproduct-label">
            Product Name *
          </label>

          <input
            type="text"
            name="name"
            value={product.name}
            onChange={
              handleChange
            }
            className="editproduct-input"
            placeholder="e.g. Banarasi Silk Saree"
          />

          <label className="editproduct-label">
            Description *
          </label>

          <textarea
            name="description"
            value={
              product.description
            }
            onChange={
              handleChange
            }
            className="editproduct-textarea"
            placeholder="Product description..."
          />
        </div>

        {/* ================= CATEGORY & PRICING ================= */}

        <div className="editproduct-form-card">
          <h3>
            Category & Pricing
          </h3>

          <div className="editproduct-grid-2">
            {/* CATEGORY */}

            <div>
              <label className="editproduct-label">
                Category *
              </label>

              <select
                name="categoryId"
                value={
                  categoryIdString
                }
                onChange={
                  handleCategoryChange
                }
                className="editproduct-select"
              >
                {!isKnownCategory &&
                  categoryIdString && (
                    <option
                      value={
                        categoryIdString
                      }
                    >
                      {product.category ||
                        `Category ${categoryIdString}`}
                    </option>
                  )}

                <option value="">
                  Select Category
                </option>

                {CATEGORY_OPTIONS.map(
                  (item) => (
                    <option
                      key={item.id}
                      value={item.id}
                    >
                      {item.name}
                    </option>
                  )
                )}
              </select>
            </div>



            {/* ORIGINAL PRICE */}

            <div>
              <label className="editproduct-label">
                Price *
              </label>

              <input
                type="number"
                name="originalPrice"
                value={
                  product.originalPrice
                }
                onChange={
                  handleChange
                }
                className="editproduct-input"
                placeholder="12500"
              />
            </div>

            {/* STOCK */}

            <div>
              <label className="editproduct-label">
                Stock *
              </label>

              <input
                type="number"
                name="stock"
                value={
                  product.stock
                }
                onChange={
                  handleChange
                }
                className="editproduct-input"
                placeholder="10"
              />
            </div>

            {/* DISCOUNT */}

            <div>
              <label className="editproduct-label">
                Discount %
              </label>

              <input
                type="number"
                name="discountPercentage"
                value={product.discountPercentage}
                onChange={handleChange}
                className="editproduct-input"
                placeholder="0"
                min="0"
                max="100"
                step="0.01"
              />
            </div>

            {/* FINAL PRICE */}

            <div>
              <label className="editproduct-label">
                Final Price
              </label>

              <input
                type="text"
                value={
                  originalPrice > 0
                    ? `₹${Math.round(calculatedFinalPrice)}`
                    : "₹0"
                }
                readOnly
                className="editproduct-input editproduct-readonly"
              />

              {discountPercentage > 0 &&
                originalPrice > 0 && (
                  <small className="editproduct-price-saving">
                    You save ₹{Math.round(discountAmount)}
                  </small>
                )}
            </div>
          </div>
        </div>

        {/* ================= STOCK DETAILS ================= */}

        <div className="editproduct-form-card">
          <h3>
            Stock Details
          </h3>

          <div className="editproduct-grid-2">
            {/* SKU */}

            <div>
              <label className="editproduct-label">
                SKU
              </label>

              <input
                type="text"
                name="sku"
                value={
                  product.sku
                }
                onChange={
                  handleChange
                }
                className="editproduct-input"
                placeholder="SKU"
              />
            </div>



            {/* FABRIC */}

            <div>
              <label className="editproduct-label">
                Fabric Type
              </label>

              <input
                type="text"
                name="material"
                value={
                  product.material
                }
                onChange={
                  handleChange
                }
                className="editproduct-input"
                placeholder="Material"
              />
            </div>

            {/* STATUS */}

            <div>
              <label className="editproduct-label">
                Status *
              </label>

              <select
                name="status"
                value={
                  product.status
                }
                onChange={
                  handleChange
                }
                className="editproduct-select"
              >
                <option value={1}>
                  Active
                </option>

                <option value={2}>
                  Out Of Stock
                </option>
              </select>
            </div>

            {/* TAG */}

            <div>
              <label className="editproduct-label">
                Tag
              </label>

              <input
                type="text"
                name="tag"
                value={
                  product.tag
                }
                onChange={
                  handleChange
                }
                className="editproduct-input"
                placeholder="New / Bestseller / Trending"
              />
            </div>
          </div>

          {/* ================= COLORS ================= */}

          <div className="editproduct-color-field">
            <label className="editproduct-label">
              Colors
            </label>

            <button
              type="button"
              className={`editproduct-color-trigger ${colorDropdownOpen
                ? "open"
                : ""
                }`}
              onClick={() =>
                setColorDropdownOpen(
                  (prev) =>
                    !prev
                )
              }
            >
              <span>
                {selectedColorNames.length >
                  0
                  ? `${selectedColorNames.length
                  } color${selectedColorNames.length >
                    1
                    ? "s"
                    : ""
                  } selected`
                  : "Select colors"}
              </span>

              <span className="editproduct-color-arrow">
                ⌄
              </span>
            </button>

            {/* SELECTED COLORS */}

            {selectedColorNames.length >
              0 && (
                <div className="editproduct-selected-colors">
                  {selectedColorNames.map(
                    (colorName) => {
                      const option =
                        colorOptions.find(
                          (item) =>
                            item.name.toLowerCase() ===
                            colorName.toLowerCase()
                        );

                      return (
                        <span
                          key={
                            colorName
                          }
                          className="editproduct-color-pill"
                        >
                          <span
                            className="editproduct-color-swatch"
                            style={{
                              backgroundColor:
                                option?.hex ||
                                colorName,
                            }}
                          />

                          {colorName}
                        </span>
                      );
                    }
                  )}
                </div>
              )}

            {/* COLOR DROPDOWN */}

            {colorDropdownOpen && (
              <div className="editproduct-color-menu">
                <div className="editproduct-color-list">
                  {colorOptions.map(
                    (option) => (
                      <label
                        className="editproduct-color-check"
                        key={
                          option.name
                        }
                      >
                        <input
                          type="checkbox"
                          checked={selectedColorNames.includes(
                            option.name
                          )}
                          onChange={() =>
                            handleColorToggle(
                              option.name
                            )
                          }
                        />

                        <span
                          className="editproduct-color-swatch"
                          style={{
                            backgroundColor:
                              option.hex,
                          }}
                        />

                        <span>
                          {
                            option.name
                          }
                        </span>
                      </label>
                    )
                  )}
                </div>

                {/* ADD CUSTOM COLOR */}

                <div className="editproduct-add-color">
                  <input
                    type="text"
                    className="editproduct-custom-color-name"
                    value={
                      customColor
                    }
                    onChange={(e) =>
                      setCustomColor(
                        e.target
                          .value
                      )
                    }
                    placeholder="Add color name"
                  />

                  <input
                    type="color"
                    className="editproduct-custom-color-picker"
                    value={
                      customColorHex
                    }
                    onChange={(e) =>
                      setCustomColorHex(
                        e.target
                          .value
                      )
                    }
                    aria-label="Choose custom color"
                  />

                  <button
                    type="button"
                    onClick={
                      handleAddColor
                    }
                  >
                    + Add Color
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ================= SIZES ================= */}

          <div className="editproduct-size-field">
            <label className="editproduct-label">
              Sizes
            </label>

            <div className="editproduct-size-options">
              {[
                "XS",
                "S",
                "M",
                "L",
                "XL",
                "XXL",
              ].map((size) => (
                <label
                  key={size}
                  className="editproduct-size-checkbox"
                >
                  <input
                    type="checkbox"
                    checked={product.sizes.includes(
                      size
                    )}
                    onChange={(e) => {
                      const checked =
                        e.target
                          .checked;

                      setProduct(
                        (prev) => ({
                          ...prev,

                          sizes:
                            checked
                              ? [
                                ...prev.sizes,
                                size,
                              ]
                              : prev.sizes.filter(
                                (
                                  s
                                ) =>
                                  s !==
                                  size
                              ),
                        })
                      );
                    }}
                  />

                  <span>
                    {size}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ================= DETAILS ================= */}

        <div className="editproduct-form-card editproduct-details-card">
          <div className="editproduct-details-heading">
            <div>
              <h3>
                Details
              </h3>

              <p>
                Add the product information shown in the Details section on the product page.
              </p>
            </div>
          </div>

          <div className="editproduct-grid-2">
            {/* LENGTH */}

            <div>
              <label className="editproduct-label">
                Length
              </label>

              <input
                type="text"
                className="editproduct-input"
                name="length"
                value={
                  product.details
                    .length
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. 0.80 m (80 cm)"
              />
            </div>

            {/* WASH CARE */}

            <div>
              <label className="editproduct-label">
                Wash Care
              </label>

              <input
                type="text"
                className="editproduct-input"
                name="washCare"
                value={
                  product.details
                    .washCare
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. Dry Wash"
              />
            </div>

            {/* SLEEVES */}

            <div>
              <label className="editproduct-label">
                Sleeves
              </label>

              <input
                type="text"
                className="editproduct-input"
                name="sleeves"
                value={
                  product.details
                    .sleeves
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. Sleeveless"
              />
            </div>

            {/* NECKLINE */}

            <div>
              <label className="editproduct-label">
                Neckline
              </label>

              <input
                type="text"
                className="editproduct-input"
                name="neckline"
                value={
                  product.details
                    .neckline
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. U Neck"
              />
            </div>

            {/* DRESS INFO */}

            <div className="editproduct-full-width">
              <label className="editproduct-label">
                Dress / Model Info
              </label>

              <input
                type="text"
                className="editproduct-input"
                name="dressInfo"
                value={
                  product.details
                    .dressInfo
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. The model is wearing a S size dress"
              />
            </div>

            {/* WHAT YOU WILL RECEIVE */}

            <div className="editproduct-full-width">
              <label className="editproduct-label">
                What You Will Receive
              </label>

              <input
                type="text"
                className="editproduct-input"
                name="whatYouWillReceive"
                value={
                  product.details
                    .whatYouWillReceive
                }
                onChange={
                  handleDetailChange
                }
                placeholder="e.g. 1 Dress"
              />
            </div>

            {/* DISCLAIMER */}

            <div className="editproduct-full-width">
              <label className="editproduct-label">
                Disclaimer
              </label>

              <textarea
                className="editproduct-textarea"
                name="disclaimer"
                value={
                  product.details
                    .disclaimer
                }
                onChange={
                  handleDetailChange
                }
                placeholder="Pictures are clicked in daylight. Color may vary slightly due to screen brightness."
              />
            </div>
          </div>

          {(product.details.customDetails || []).length > 0 && (
            <div className="editproduct-custom-details-list">
              {(product.details.customDetails || []).map((detail, index) => (
                <div
                  className="editproduct-custom-detail-row"
                  key={`custom-detail-${index}`}
                >
                  <div>
                    <label className="editproduct-label">
                      Detail Name
                    </label>
                    <input
                      type="text"
                      className="editproduct-input"
                      value={detail.label}
                      onChange={(e) =>
                        handleCustomDetailChange(index, "label", e.target.value)
                      }
                      placeholder="e.g. Occasion"
                    />
                  </div>

                  <div>
                    <label className="editproduct-label">
                      Detail Value
                    </label>
                    <input
                      type="text"
                      className="editproduct-input"
                      value={detail.value}
                      onChange={(e) =>
                        handleCustomDetailChange(index, "value", e.target.value)
                      }
                      placeholder="e.g. Wedding / Festive"
                    />
                  </div>

                  <button
                    type="button"
                    className="editproduct-remove-custom-detail-btn"
                    onClick={() => handleRemoveCustomDetail(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            className="editproduct-add-new-detail-btn"
            onClick={handleAddCustomDetail}
          >
            + Add New Detail
          </button>
        </div>

        {/* ================= BUTTONS ================= */}

        <div className="editproduct-actions">
          <button
            type="button"
            className="editproduct-cancel"
            onClick={() =>
              navigate(
                `/product-details/${id}`
              )
            }
          >
            Cancel
          </button>

          <button
            type="button"
            className="editproduct-save"
            onClick={
              handleSaveClick
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M12.6667 2.5C13.1063 2.50626 13.5256 2.68598 13.8333 3L17 6.16667C17.314 6.47438 17.4937 6.89372 17.5 7.33333V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H12.6667Z"
                stroke="white"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              <path
                d="M14.1668 17.5V11.6667C14.1668 11.4457 14.079 11.2337 13.9228 11.0774C13.7665 10.9211 13.5545 10.8333 13.3335 10.8333H6.66683C6.44582 10.8333 6.23385 10.9211 6.07757 11.0774C5.92129 11.2337 5.8335 11.4457 5.8335 11.6667V17.5"
                stroke="white"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              <path
                d="M5.8335 2.5V5.83333C5.8335 6.05435 5.92129 6.26631 6.07757 6.42259C6.23385 6.57887 6.44582 6.66667 6.66683 6.66667H12.5002"
                stroke="white"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            Save Changes
          </button>
        </div>
      </div>

      {/* ================= SUCCESS POPUP ================= */}

      {showPopup && (
        <div className="editproduct-popup-overlay">
          <div className="editproduct-popup-box">
            <div className="editproduct-popup-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="30px"
                viewBox="0 -960 960 960"
                width="30px"
                fill="#16a34a"
              >
                <path d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q65 0 123 19t107 53l-58 59q-38-24-81-37.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160q133 0 226.5-93.5T800-480q0-18-2-36t-6-35l65-65q11 32 17 66t6 70q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm-56-216L254-466l56-56 114 114 400-401 56 56-456 457Z" />
              </svg>
            </div>

            <h3>
              Changes Saved Successfully....!
            </h3>

            <p>
              Product has been updated.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProduct;